import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Home, MessageCircle, Zap, PackageX, Cable, Terminal } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/use-seo";
import { useLang } from "@/i18n/LanguageContext";
import type { Lang } from "@/i18n/translations";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { trackCtaClick } from "@/lib/analytics";

// Page-local i18n — kept inline like other secondary pages.
type Copy = Record<Lang, string>;
const C = (en: string, es: string, pt: string): Copy => ({ en, es, pt });

const COPY = {
  eyebrow: C("Error 404 · Package Lost in Transit", "Error 404 · Paquete perdido en tránsito", "Erro 404 · Pacote perdido em trânsito"),
  title: C(
    "This page tripped the breaker.",
    "Esta página disparó el interruptor.",
    "Esta página disparou o disjuntor.",
  ),
  body: C(
    "Looks like this URL got rerouted to a warehouse in the middle of nowhere. Our drones are circling, but the page is officially out of stock. Reset the breaker below — or let us source what you actually need.",
    "Parece que esta URL fue redirigida a un almacén en medio de la nada. Nuestros drones están dando vueltas, pero la página está oficialmente agotada. Reinicia el interruptor abajo — o déjanos suministrar lo que realmente necesitas.",
    "Parece que esta URL foi redirecionada para um armazém no meio do nada. Nossos drones estão circulando, mas a página está oficialmente esgotada. Reinicie o disjuntor abaixo — ou deixe-nos fornecer o que você realmente precisa.",
  ),
  pathLabel: C("Requested path", "Ruta solicitada", "Caminho solicitado"),
  ctaHome: C("Reset the breaker", "Reiniciar el interruptor", "Reiniciar o disjuntor"),
  ctaQuote: C("Reroute to a quote", "Redirigir a una cotización", "Redirecionar para uma cotação"),
  ctaWhatsApp: C("Ping us on WhatsApp", "Escríbenos por WhatsApp", "Chame no WhatsApp"),
  diagTitle: C("System diagnostic", "Diagnóstico del sistema", "Diagnóstico do sistema"),
  diag1Label: C("Voltage at this URL", "Voltaje en esta URL", "Tensão nesta URL"),
  diag1Value: C("0 V — flatlined", "0 V — sin señal", "0 V — sem sinal"),
  diag2Label: C("Inventory status", "Estado de inventario", "Status do inventário"),
  diag2Value: C("Backordered indefinitely", "En espera indefinidamente", "Em espera indefinida"),
  diag3Label: C("ETA", "Tiempo estimado", "Prazo"),
  diag3Value: C("When pigs ship via DHL", "Cuando los cerdos envíen por DHL", "Quando porcos enviarem via DHL"),
  diag4Label: C("Root cause", "Causa raíz", "Causa raiz"),
  diag4Value: C("PEBKAC or rogue router", "PEBKAC o router rebelde", "PEBKAC ou roteador rebelde"),
  funFact: C(
    "Fun fact: 99.97% of our real shipments arrive. This page is in the other 0.03%.",
    "Dato curioso: 99,97% de nuestros envíos reales llegan. Esta página está en el otro 0,03%.",
    "Curiosidade: 99,97% dos nossos envios reais chegam. Esta página está nos outros 0,03%.",
  ),
  metaTitle: C(
    "Page Not Found (404) | Switzh",
    "Página No Encontrada (404) | Switzh",
    "Página Não Encontrada (404) | Switzh",
  ),
  metaDesc: C(
    "This page doesn't exist. Return home or request pricing on industrial electrical components.",
    "Esta página no existe. Vuelve al inicio o solicita precio de componentes eléctricos industriales.",
    "Esta página não existe. Volte ao início ou solicite preço de componentes elétricos industriais.",
  ),
} as const;

const NotFound = () => {
  const location = useLocation();
  const { lang, localePath } = useLang();
  const tx = (k: keyof typeof COPY) => COPY[k][lang];

  useEffect(() => {
    // Surfaces broken-link signal for ops without polluting analytics dashboards.
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  useSeo({
    title: tx("metaTitle"),
    description: tx("metaDesc"),
    lang,
  });

  // Robots: 404s should not be indexed.
  useEffect(() => {
    const prev = document.querySelector('meta[name="robots"]');
    const previousContent = prev?.getAttribute("content");
    let tag = prev as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", "noindex, follow");
    return () => {
      if (previousContent && tag) tag.setAttribute("content", previousContent);
    };
  }, []);

  const whatsAppHref = buildWhatsAppUrl(
    lang === "es"
      ? "Hola, llegué a una página inexistente en su sitio. Necesito una cotización."
      : lang === "pt"
      ? "Olá, cheguei a uma página inexistente no site. Preciso de uma cotação."
      : "Hi, I landed on a missing page on your site. I'd like a quote.",
  );

  const diagnostics = [
    { label: tx("diag1Label"), value: tx("diag1Value"), icon: Zap },
    { label: tx("diag2Label"), value: tx("diag2Value"), icon: PackageX },
    { label: tx("diag3Label"), value: tx("diag3Value"), icon: Cable },
    { label: tx("diag4Label"), value: tx("diag4Value"), icon: Terminal },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />

      <main className="relative flex flex-1 items-center overflow-hidden">
        {/* Ambient grid + glow — gives the page a "control panel" vibe */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse at center, hsl(0 0% 0% / 0.85), transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, hsl(0 0% 0% / 0.85), transparent 70%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/3 -z-0 h-[420px] w-[420px] -translate-x-1/2 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--primary) / 0.22), transparent 70%)",
          }}
        />

        <div className="container relative z-10 px-4 py-14 md:py-20">
          <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
            {/* Left: hero copy */}
            <div className="text-center lg:text-left">
              <p className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                <Zap className="h-3.5 w-3.5" aria-hidden />
                {tx("eyebrow")}
              </p>

              <h1 className="mt-5 text-balance font-bold tracking-tight text-foreground">
                <span
                  className="block bg-clip-text text-[5.5rem] font-extrabold leading-[0.9] text-transparent sm:text-[7rem] md:text-[8.5rem]"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary-hover)) 60%, hsl(var(--foreground)) 120%)",
                  }}
                >
                  4<span className="inline-block animate-pulse">0</span>4
                </span>
                <span className="mt-5 block text-2xl sm:text-3xl md:text-4xl">
                  {tx("title")}
                </span>
              </h1>

              <p className="mx-auto mt-5 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
                {tx("body")}
              </p>

              {location.pathname && location.pathname !== "/" && (
                <div className="mt-6 inline-flex max-w-full items-center gap-2 overflow-hidden rounded-md border border-border bg-muted/60 px-3 py-1.5 font-mono text-xs text-muted-foreground">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-destructive" aria-hidden />
                  <span className="text-muted-foreground/70">GET</span>
                  <span className="truncate text-foreground">{location.pathname}</span>
                  <span className="ml-1 rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-destructive">
                    404
                  </span>
                </div>
              )}

              <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center lg:justify-start">
                <Button
                  asChild
                  size="lg"
                  onClick={() => trackCtaClick("not_found", "404_home")}
                >
                  <Link to={localePath("/")} aria-label={tx("ctaHome")}>
                    <Home className="mr-2 h-4 w-4" />
                    {tx("ctaHome")}
                  </Link>
                </Button>

                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  onClick={() => trackCtaClick("not_found", "404_quote")}
                >
                  <Link to={localePath("/request-quote")}>
                    <ArrowLeft className="mr-2 h-4 w-4 rotate-180" />
                    {tx("ctaQuote")}
                  </Link>
                </Button>

                <Button
                  asChild
                  variant="ghost"
                  size="lg"
                  onClick={() => trackCtaClick("not_found", "404_whatsapp")}
                >
                  <a href={whatsAppHref} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    {tx("ctaWhatsApp")}
                  </a>
                </Button>
              </div>

              <p className="mt-6 text-xs italic text-muted-foreground/80">
                {tx("funFact")}
              </p>
            </div>

            {/* Right: terminal-style diagnostic card */}
            <div className="relative">
              <div className="rounded-xl border border-border bg-card/80 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-card/60">
                {/* terminal header */}
                <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-destructive/80" aria-hidden />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" aria-hidden />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" aria-hidden />
                  <span className="ml-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    switzh@ops:~$ {tx("diagTitle")}
                  </span>
                </div>

                <ul className="divide-y divide-border">
                  {diagnostics.map(({ label, value, icon: Icon }) => (
                    <li
                      key={label}
                      className="flex items-start gap-3 px-4 py-3.5 font-mono text-sm"
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          {label}
                        </p>
                        <p className="mt-0.5 truncate text-foreground">{value}</p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-border px-4 py-3 font-mono text-xs text-muted-foreground">
                  <span className="text-emerald-500">$</span> reroute --to=/request-quote
                  <span className="ml-1 inline-block h-3.5 w-1.5 translate-y-0.5 animate-pulse bg-primary" aria-hidden />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
